'use client';

import Link from 'next/link';
import { useEvents } from '@/lib/hooks/useSiteData';

const CommunitySection = () => {
  const { data: eventsData, loading, error } = useEvents(true);

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate().toString()
    };
  };

  // Helper function to format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Helper function to get area from address
  const getArea = (address: any) => {
    if (address?.city && address?.state) {
      return `${address.city} Area`;
    }
    return 'Local Area';
  };

  // Show loading state
  if (loading) {
    return (
      <section className="community-sec">
        <div className="container">
          <div className="row top">
            <div className="col-lg-6">
              <div className="connect-heading">
                <div className="placeholder-glow">
                  <h2 className="placeholder col-8"></h2>
                </div>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end">
              <div className="placeholder" style={{ width: '200px', height: '40px', borderRadius: '20px' }}></div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-5 order-lg-last">
              <div className="comunity-img">
                <div className="placeholder" style={{ width: '100%', height: '300px', borderRadius: '8px' }}></div>
                <div className="img-content">
                  <h4 className="placeholder col-10 mb-2"></h4>
                  <div className="time placeholder col-6 mb-1"></div>
                  <div className="area placeholder col-8"></div>
                </div>
                <div className="date-box">
                  <span className="placeholder col-4"></span>
                  <span className="placeholder col-2"></span>
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="community-box">
                  <div className="date-box">
                    <span className="placeholder col-4"></span>
                    <span className="placeholder col-2"></span>
                  </div>
                  <div className="comunity-content">
                    <h5 className="placeholder col-10 mb-2"></h5>
                    <div className="time placeholder col-6 mb-1"></div>
                    <div className="area placeholder col-8"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="community-sec">
        <div className="container">
          <div className="row top">
            <div className="col-lg-6">
              <div className="connect-heading">
                <h2>Community <span>Calendar</span></h2>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end">
              <a href="/en/find-events" className="btn btn-secondary">
                <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                Find Events Near You
              </a>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="alert alert-warning" role="alert">
                Unable to load events. Please try again later.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Process events data
  const events = eventsData?.data || [];
  const featuredEvent = events[0]; // First event as featured
  const otherEvents = events.slice(1, 4); // Next 3 events for the list

  // Fallback static data if no events
  const fallbackFeaturedEvent = {
    id: 'fallback-1',
    name: "Richmond/Philadelphia PADRECC Virtual Parkinson's Disease",
    timing: {
      startDate: "2025-12-13T20:00:00.734Z",
      endDate: "2025-12-13T21:00:00.736Z"
    },
    contactInformations: {
      address: {
        city: "Charlottesville",
        state: "Virginia"
      }
    },
    mainImage: "/images/boxes/community_img.jpg"
  };

  const fallbackEvents = [
    {
      id: 'fallback-2',
      name: "Complimentary Pain and Injury Screenings",
      timing: {
        startDate: "2025-12-14T09:00:00.000Z",
        endDate: "2025-12-14T10:00:00.000Z"
      },
      contactInformations: {
        address: {
          city: "Charlottesville",
          state: "Virginia"
        }
      }
    },
    {
      id: 'fallback-3',
      name: "Senior Health & Wellness Fair",
      timing: {
        startDate: "2025-12-15T10:00:00.000Z",
        endDate: "2025-12-15T14:00:00.000Z"
      },
      contactInformations: {
        address: {
          city: "Richmond",
          state: "Virginia"
        }
      }
    },
    {
      id: 'fallback-4',
      name: "Memory Care Support Group",
      timing: {
        startDate: "2025-12-16T14:00:00.000Z",
        endDate: "2025-12-16T15:00:00.000Z"
      },
      contactInformations: {
        address: {
          city: "Norfolk",
          state: "Virginia"
        }
      }
    }
  ];

  const displayFeaturedEvent = featuredEvent || fallbackFeaturedEvent;
  const displayEvents = otherEvents.length > 0 ? otherEvents : fallbackEvents;

  return (
    <section className="community-sec">
      <div className="container">
        <div className="row top">
          <div className="col-lg-6">
            <div className="connect-heading">
              <h2>Community <span>Calendar</span></h2>
            </div>
          </div>
          <div className="col-lg-6 d-flex justify-content-lg-end">
            <Link href="/en/find-events" className="btn btn-secondary">
              <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
              Find Events Near You
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-5 order-lg-last">
            <div className="comunity-img">
              <img 
                src={displayFeaturedEvent.mainImage || "/images/boxes/community_img.jpg"} 
                alt={displayFeaturedEvent.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/boxes/community_img.jpg";
                }}
              />
              <div className="img-content">
                <h4>
                  <Link href={`/en/events/${displayFeaturedEvent.id}`}>{displayFeaturedEvent.name}</Link>
                </h4>
                <div className="time">
                  {formatTime(displayFeaturedEvent.timing.startDate)} - {formatTime(displayFeaturedEvent.timing.endDate)}
                </div>
                <div className="area">{getArea(displayFeaturedEvent.contactInformations?.address)}</div>
              </div>
              <div className="date-box">
                <span>{formatDate(displayFeaturedEvent.timing.startDate).month}</span>
                <span>{formatDate(displayFeaturedEvent.timing.startDate).day}</span>
              </div>
            </div>
          </div>
          <div className="col-lg-7">
            {displayEvents.map((event: any) => (
              <div key={event.id} className="community-box">
                <div className="date-box">
                  <span>{formatDate(event.timing.startDate).month}</span>
                  <span>{formatDate(event.timing.startDate).day}</span>
                </div>
                <div className="comunity-content">
                  <h5>
                    <Link href={`/en/events/${event.id}`}>{event.name}</Link>
                  </h5>
                  <div className="time">
                    {formatTime(event.timing.startDate)} - {formatTime(event.timing.endDate)}
                  </div>
                  <div className="area">{getArea(event.contactInformations?.address)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunitySection;
